import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { OrderHistoryItem, PaymentFlow } from '@/types';
import type { PaymentData } from '@/contexts/CheckoutContext';
import type { InvoiceData } from '@/components/invoice/types';
import { ROUTES } from '@/config/routes';

interface UseReceiptActionsOptions {
  setOrderData: (data: PaymentData | null) => void;
  setPaymentFlow: (flow: PaymentFlow) => void;
  setSelectedOrderForReceipt: (order: OrderHistoryItem | null) => void;
  setShowOrderHistory: (show: boolean) => void;
  setInvoiceData: (data: InvoiceData | null) => void;
  onReceiptClose: () => void;
}

// OrderHistoryItem에서 PaymentData로 변환
const convertToOrderData = (order: OrderHistoryItem): PaymentData => ({
  // #632 F-18: 목록은 orderNumber(ORD-...)를 표시하는데 영수증이 내부 id(order-154)를
  // 쓰면 동일 주문의 번호가 화면마다 어긋난다 — 서버 주문번호 우선, 없으면 내부 id.
  orderId: order.orderNumber || order.id,
  timestamp: order.date,
  restaurant: order.restaurant,
  tableNumber: order.tableNumber,
  items: order.items.map((item, index) => ({
    id: `${order.id}-item-${index}`,
    name: item.name,
    description: '',
    price: item.price,
    quantity: item.quantity,
    category: '',
    restaurantId: order.restaurantId,
    restaurantName: order.restaurant,
  })),
  grossSubtotal: order.grossSubtotal,
  netSubtotal: order.netSubtotal,
  tax: order.tax,
  tip: order.tip,
  total: order.total,
  paymentMethod: order.paymentMethod
});

export function useReceiptActions(options: UseReceiptActionsOptions) {
  const navigate = useNavigate();

  const {
    setOrderData,
    setPaymentFlow,
    setSelectedOrderForReceipt,
    setShowOrderHistory,
    setInvoiceData,
    onReceiptClose,
  } = options;

  // 영수증 뒤로가기 핸들러
  const handleReceiptBackFromHistory = useCallback((selectedOrderForReceipt: OrderHistoryItem | null) => {
    if (selectedOrderForReceipt) {
      setPaymentFlow('idle');
      setSelectedOrderForReceipt(null);
      setShowOrderHistory(true);
    } else {
      onReceiptClose();
    }
  }, [setPaymentFlow, setSelectedOrderForReceipt, setShowOrderHistory, onReceiptClose]);

  // 주문 내역에서 영수증 보기
  const handleViewReceiptFromHistory = useCallback((order: OrderHistoryItem) => {
    setOrderData(convertToOrderData(order));
    setSelectedOrderForReceipt(order);
    setShowOrderHistory(false);
    setPaymentFlow('success');
  }, [setOrderData, setSelectedOrderForReceipt, setShowOrderHistory, setPaymentFlow]);

  // 주문 내역에서 세금계산서 요청 (URL 네비게이션)
  const handleRequestTaxInvoiceFromHistory = useCallback((order: OrderHistoryItem) => {
    setOrderData(convertToOrderData(order));
    setShowOrderHistory(false);
    navigate(ROUTES.COMPANY_LIST);
  }, [setOrderData, setShowOrderHistory, navigate]);

  // 완료된 세금계산서 보기
  const handleViewCompletedTaxInvoiceFromHistory = useCallback((order: OrderHistoryItem) => {
    if (order.completedTaxInvoiceData) {
      setInvoiceData(order.completedTaxInvoiceData as unknown as InvoiceData);
      setOrderData(convertToOrderData(order));
      setShowOrderHistory(false);
      setPaymentFlow('complete');
    }
  }, [setInvoiceData, setOrderData, setShowOrderHistory, setPaymentFlow]);

  return {
    handleReceiptBackFromHistory,
    handleViewReceiptFromHistory,
    handleRequestTaxInvoiceFromHistory,
    handleViewCompletedTaxInvoiceFromHistory,
  };
}
